import React, { useEffect } from "react";
import { EditOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { Button, Form, Image, Input, Popconfirm, FormInstance } from "antd";
import TextArea from "antd/es/input/TextArea";

import ButtonWrapper from "../../components/reuseComp/ButtonWrapper";
import UploadImage from "../../components/UploadImage";

interface TestimononialItem {
  name: string;
  location: string;
  image: string;
  content: string;
}

interface OurTransformationProps {
  heading?: string;
  subtext?: string;
  testimonial?: TestimononialItem[];
}

interface Props {
  isEditing: boolean;
  setIsEditing: (v: boolean) => void;
  handleCancel: () => void;
  handleSave: () => void;
  onDelete: (index: number, field: "Our Transformation") => void;
  form: FormInstance;
  ourTransformationData?: OurTransformationProps;
  loading?: boolean;
  mode: "list" | "add" | "edit";
  setMode: (value: "list" | "add" | "edit") => void;
  editingIndex: number | null;
  setEditingIndex: (value: number | null) => void;
}

const OurTransformation: React.FC<Props> = ({
  isEditing,
  setIsEditing,
  handleCancel,
  handleSave,
  form,
  ourTransformationData,
  onDelete,
  loading,
  mode,
  setMode,
  editingIndex,
  setEditingIndex,
}) => {
  useEffect(() => {
    if (!ourTransformationData) return;

    if (mode === "list") {
      form.setFieldsValue({
        our_transformation: {
          heading: ourTransformationData.heading ?? "",
          subtext: ourTransformationData.subtext ?? "",
          testimonial: ourTransformationData.testimonial ?? [],
        },
      });
    }

    if (mode === "add") {
      form.setFieldsValue({
        our_transformation: {
          testimonial: [{ name: "", content: "", image: "", location: "" }],
        },
      });
    }

    if (mode === "edit" && editingIndex !== null) {
      const item = ourTransformationData.testimonial?.[editingIndex];
      if (item) {
        form.setFieldsValue({
          our_transformation: {
            testimonial: [item],
          },
        });
      }
    }
  }, [mode, editingIndex, ourTransformationData, form]);

  const testimonial = ourTransformationData?.testimonial ?? [];

  return (
    <div>
      {isEditing && (
        <div className="bg-[#F9F9F9] p-5 rounded-xl mb-4">
          <Form layout="vertical" form={form}>
            <Form.Item
              label="Heading"
              name={["our_transformation", "heading"]}
              rules={[{ required: true, message: "Enter heading" }]}
            >
              <Input placeholder="Enter heading..." />
            </Form.Item>

            <Form.Item
              label="Subtext"
              name={["our_transformation", "subtext"]}
              rules={[{ required: true, message: "Enter subtext" }]}
            >
              <TextArea rows={3} placeholder="Enter subtext..." />
            </Form.Item>
          </Form>

          <div className="flex gap-3 mt-4">
            <ButtonWrapper
              type="primary"
              onClick={handleSave}
              loading={loading}
            >
              Save
            </ButtonWrapper>
            <ButtonWrapper
              onClick={() => {
                setIsEditing(false);
                handleCancel();
              }}
            >
              Cancel
            </ButtonWrapper>
          </div>
        </div>
      )}

      {mode === "list" && !isEditing && (
        <div className="rounded-[20px] overflow-hidden">
          <div className="hidden sm:block">
            <table className="min-w-full">
              <thead>
                <tr className="bg-primary text-white">
                  <th className="py-3 px-4 text-left">Heading</th>
                  <th className="py-3 px-4 text-left">Subtext</th>
                  <th className="py-3 px-4 text-right">Action</th>
                </tr>
              </thead>

              <tbody className="bg-[#F9F9F9]">
                {ourTransformationData?.heading ? (
                  <tr className="border-b">
                    <td className="px-4 py-3">
                      {ourTransformationData?.heading}
                    </td>
                    <td className="px-4 py-3">
                      {ourTransformationData?.subtext}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <EditOutlined
                        className="text-primary text-xl cursor-pointer"
                        onClick={() => setIsEditing(true)}
                      />
                    </td>
                  </tr>
                ) : (
                  <tr>
                    <td colSpan={4} className="text-center py-6">
                      <Button type="dashed" onClick={() => setIsEditing(true)}>
                        Add Data
                      </Button>
                    </td>
                  </tr>
                )}

                <tr>
                  <td colSpan={4} className="px-4 py-3 text-right">
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={() => {
                        setMode("add");
                        setEditingIndex(null);
                      }}
                    >
                      Add Testimonial
                    </Button>
                  </td>
                </tr>

                <tr>
                  <td colSpan={4} className="p-4">
                    <table className="min-w-full">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="px-4 py-2 text-left">Avatar</th>
                          <th className="px-4 py-2 text-left">Author</th>
                          <th className="px-4 py-2 text-left">Location</th>
                          <th className="px-4 py-2 text-left">Description</th>
                          <th className="px-4 py-2 text-right">Action</th>
                        </tr>
                      </thead>

                      <tbody>
                        {testimonial.length ? (
                          testimonial.map((item, index) => (
                            <tr key={index} className="border-t">
                              <td className="px-4 py-2">
                                <Image
                                  src={item.image}
                                  width={60}
                                  height={60}
                                  className="rounded-full"
                                />
                              </td>
                              <td className="px-4 py-2">{item.name}</td>
                              <td className="px-4 py-2">{item.location}</td>
                              <td className="px-4 py-2">{item.content}</td>
                              <td className="px-4 py-2 text-right">
                                <div className="flex justify-end gap-4">
                                  <EditOutlined
                                    className="cursor-pointer text-primary"
                                    onClick={() => {
                                      setMode("edit");
                                      setEditingIndex(index);
                                    }}
                                  />
                                  <Popconfirm
                                    title="Delete this testimonial?"
                                    onConfirm={() =>
                                      onDelete(index, "Our Transformation")
                                    }
                                  >
                                    <DeleteOutlined className="cursor-pointer text-red-500" />
                                  </Popconfirm>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan={5}
                              className="text-center py-6 text-gray-500"
                            >
                              No testimonial added
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Mobile View */}
          <div className="sm:hidden space-y-4">
            {ourTransformationData?.heading ? (
              <div className="bg-[#F9F9F9] p-4 rounded-xl shadow-sm">
                <div>
                  <span className="font-semibold text-primary">Heading: </span>
                  <p className="">
                    {ourTransformationData.heading}
                  </p>
                </div>
                <div>
                  <span className="font-semibold text-primary">Subtext: </span>
                  <p className="">{ourTransformationData.subtext}</p>
                </div>

                <div>
                  <span className="font-semibold text-primary block mt-2 mb-1">
                    Action:
                  </span>
                  <EditOutlined
                    onClick={() => setIsEditing(true)}
                    className="cursor-pointer text-black text-[18px]"
                  />
                </div>
              </div>
            ) : (
              <div className="text-center py-6">
                <Button type="dashed" onClick={() => setIsEditing(true)}>
                  Add Data
                </Button>
              </div>
            )}

            {testimonial.length ? (
              testimonial.map((item, index) => (
                <div
                  key={index}
                  className="bg-[#F9F9F9] p-4 rounded-xl shadow-sm"
                >
                  <p className="text-center">
                    <Image
                      src={item.image}
                      width={60}
                      height={60}
                      className="rounded-full"
                    />
                  </p>
                  <p>
                    <span className="font-semibold text-primary">
                      Author's Name:{" "}
                    </span>{" "}
                    {item.name}
                  </p>
                  <p>
                    <span className="font-semibold text-primary">
                      Location:{" "}
                    </span>{" "}
                    {item.name}
                  </p>
                  <p>
                    <span className="font-semibold text-primary">
                      Description:{" "}
                    </span>{" "}
                    {item.content}
                  </p>
                  <div className="flex justify-end gap-3 mt-2">
                    <EditOutlined
                      className="cursor-pointer text-primary"
                      onClick={() => {
                        setMode("edit");
                        setEditingIndex(index);
                      }}
                    />
                    <Popconfirm
                      title="Delete this highlight?"
                      onConfirm={() => onDelete(index, "Our Transformation")}
                    >
                      <DeleteOutlined className="cursor-pointer text-red-500" />
                    </Popconfirm>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-gray-500 py-6">
                No testimonial added
              </div>
            )}

            <div className="text-right">
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={() => {
                  setMode("add");
                  setEditingIndex(null);
                }}
              >
                Add Testimonial
              </Button>
            </div>
          </div>
        </div>
      )}

      {mode !== "list" && !isEditing && (
        <div className="bg-[#F9F9F9] p-5 rounded-xl">
          <Form layout="vertical" form={form}>
            <Form.List name={["our_transformation", "testimonial"]}>
              {(fields) => (
                <>
                  {fields.map((field) => (
                    <div key={field.key} className="border p-4 rounded mb-4">
                      <Form.Item
                        label="Author's Avatar"
                        name={[field.name, "image"]}
                        rules={[
                          { required: true, message: "Enter author's avatar" },
                        ]}
                      >
                        <UploadImage listType="picture-card" />
                      </Form.Item>
                      <Form.Item
                        label="Author's Name"
                        name={[field.name, "name"]}
                        rules={[
                          { required: true, message: "Enter author's name" },
                        ]}
                      >
                        <Input placeholder="Enter author's name..." />
                      </Form.Item>

                      <Form.Item
                        label="Author's Location"
                        name={[field.name, "location"]}
                        rules={[{ required: true, message: "Enter location" }]}
                      >
                        <Input placeholder="Enter author's location..." />
                      </Form.Item>

                      <Form.Item
                        label="Description"
                        name={[field.name, "content"]}
                        rules={[
                          {
                            required: true,
                            message: "Enter description",
                          },
                        ]}
                      >
                        <TextArea rows={3} placeholder="Enter description..." />
                      </Form.Item>
                    </div>
                  ))}
                </>
              )}
            </Form.List>
          </Form>

          <div className="flex gap-3 mt-4">
            <ButtonWrapper
              type="primary"
              onClick={handleSave}
              loading={loading}
            >
              Save
            </ButtonWrapper>
            <ButtonWrapper
              onClick={() => {
                handleCancel();
                setMode("list");
                setEditingIndex(null);
              }}
            >
              Cancel
            </ButtonWrapper>
          </div>
        </div>
      )}
    </div>
  );
};

export default OurTransformation;
